import { sample } from 'effector';

import { CategoryPreviewResponse, SourceCatalogPage } from '@domains/categories';
import { Product } from '@domains/product';
import {
    ProductsAutocompleteRequest,
    ProductsListAvailableFilters,
    ProductsListFilters,
    ProductsListSortMode,
} from '@domains/products-catalog';
import { CategoryPageProps } from '@domains/view';

import { rootDomain } from '@stores/root';

import { apiProductsAutocomplete } from '@api/methods';
import { ProductsAutocompleteResult } from '@api/protocol';

import { NewProductsPageSlug, PromoPageSlug } from '@utils/constants';
import { pageSize } from '@utils/products-list/constants';

// --- Types ---

export type ProductsListState = {
    searchQuery: string | null;
    categorySlug: string | null;
    categories: CategoryPreviewResponse[];
    total: number;
    pageSize: number;
    page: number;
    items: Product[];
    sortMode: ProductsListSortMode;
    source: SourceCatalogPage;
    filters: ProductsListFilters;
    originalFilters: ProductsListFilters;
    availableFilters: ProductsListAvailableFilters;
};

export const productsListInitialState: ProductsListState = {
    searchQuery: null,
    categorySlug: null,
    categories: [],
    total: 0,
    page: 1,
    pageSize: pageSize,
    items: [],
    sortMode: ProductsListSortMode.Novelty,
    source: null,
    filters: {},
    originalFilters: {},
    availableFilters: {
        is_novelty: false,
        is_promo: false,
        is_ready_to_eat: false,
        min_price: null,
        max_price: null,
        countries: [],
        manufacturers: [],
    },
};

// --- Domain ---
const productsListDomain = rootDomain.createDomain();

// --- Effects ---
export const productsAutocompleteFx = productsListDomain
    .createEffect<ProductsAutocompleteRequest, ProductsAutocompleteResult>()
    .use(apiProductsAutocomplete);

// --- Events ---
export const resetProductsList = productsListDomain.createEvent();
export const initProductsList = productsListDomain.createEvent<CategoryPageProps>();
export const setSortMode = productsListDomain.createEvent<ProductsListSortMode>();
export const setFilters = productsListDomain.createEvent<ProductsListFilters>();
export const setSourceCatalogPage = productsListDomain.createEvent<SourceCatalogPage>();

// --- Stores ---
export const $productsList = productsListDomain
    .createStore<ProductsListState>(productsListInitialState, { name: 'products-list' })
    .on(setSortMode, (state, sortMode) => ({ ...state, sortMode }))
    .on(setFilters, (state, filters) => ({ ...state, filters }))
    .on(setSourceCatalogPage, (state, source) => ({ ...state, source }))
    .on(initProductsList, (_, props) => {
        let source: SourceCatalogPage = null;
        const isPromo = props.filters.is_promo;
        const isNovelty = props.filters.is_novelty;

        if (props.categorySlug === PromoPageSlug || isPromo === true) {
            source = PromoPageSlug;
        } else if (props.categorySlug === NewProductsPageSlug || isNovelty === true) {
            source = NewProductsPageSlug;
        }

        return {
            searchQuery: props.searchQuery,
            categorySlug: props.categorySlug,
            categories: props.data.categories,
            total: props.data.total,
            page: props.data.page,
            pageSize: props.data.page_size,
            items: props.data.items,
            source,
            sortMode: props.sortMode,
            filters: props.filters,
            originalFilters: props.filters,
            availableFilters: props.data.available_filters,
        };
    })
    .reset(resetProductsList);

export const $isProductsListInitializing = productsListDomain
    .createStore<boolean>(true)
    .reset(resetProductsList);

sample({
    clock: initProductsList,
    fn: () => false,
    target: $isProductsListInitializing,
});
